/**
 * ***************************************************************
 * WINDS ALOFT UTILITIES (CLIENT SIDE)
 * ***************************************************************
 *
 * PURPOSE:
 * - Fetch winds aloft text from weatherServer (Vercel)
 * - Parse FAA coded winds into usable values
 * - Map results into NavLog table format
 *
 * ***************************************************************
 */

/**
 * ---------------------------------------------------------------
 * BASE URL FOR WEATHER SERVER (VERCEL DEPLOYMENT)
 * ---------------------------------------------------------------
 * This is your deployed weather server on Vercel.
 * All fetch requests will go through this URL.
 */
const API_BASE = "https://avi-calc-server.vercel.app";

/**
 * ---------------------------------------------------------------
 * DETERMINE COLUMN POSITIONS FROM FT HEADER
 * ---------------------------------------------------------------
 */

  const getAltitudeColumnsFromHeader = () => {
  return {
    3000:  { start: 4,  end: 8  },
    6000:  { start: 9,  end: 16 },
    9000:  { start: 17, end: 24 },
    12000: { start: 25, end: 32 },
    18000: { start: 33, end: 40 },
    24000: { start: 41, end: 48 },
    30000: { start: 49, end: 55 },
    34000: { start: 56, end: 62 },
    39000: { start: 63, end: 69 },
  };
};

/**
 * ---------------------------------------------------------------
 * DECODE WIND GROUP
 * ---------------------------------------------------------------
 */

const decodeGroup = (group, altitude) => {
  const s = String(group || "").trim();

   console.log("Decoding group:", s, "at altitude:", altitude);

   // Missing or invalid
  if (!s || s.includes("/")) return null;
  if (s.length < 4) return null;

  const windPart = s.slice(0, 4);

  if (!/^\d{4}$/.test(windPart)) {
    console.warn("Unexpected wind format for group:", s);
    return null;
  }
 

  let dd = Number(windPart.slice(0, 2));
  let ff = Number(windPart.slice(2, 4));


  const tempPart = s.slice(4);
  let temp = "";
  
  // Temperature handling
  if (tempPart) {
    const clean = tempPart.replace("+", "").replace("-", "");

    if (/^\d+$/.test(clean)) {
      const isNegative = tempPart.includes("-");
      temp = (isNegative ? -1 : 1) * Number(clean);
    }
  }

  // light and variable winds 9900 or 9900+temp
  if (dd === 99 && ff === 0) {
    return {
    dir: 0,
    spd: 0,
    temp,
    raw: s,
    isLightVariable: true,
    };
  }

  // FAA winds aloft rule for 100+ kt winds
  if (dd >= 51 && dd <= 86) {
    dd = dd - 50;
    ff = ff + 100;
  }

  

  const dir = dd * 10;  // wind direction in degrees
  const spd = ff;    // wind speed in knots

  


  const result = { dir, spd, temp };

  console.log("Decoded result:", result);
  return result;
};


/**
 * ---------------------------------------------------------------
 * PARSE ONE STATION
 * REGEX: 
 * Uses the FT header row to determine altitude column positions.
 * This prevents missing 3000/6000 values from shifting 9000/12000 data left.
 * \r?\n is used to split by both Unix (\n) and Windows (\r\n) line endings.
 * ---------------------------------------------------------------
 */
export const parseStationLine = (rawText, stationId, altitudes) => {
  const lines = String(rawText || "").split(/\r?\n/); // split by newlines
  const id = String(stationId || "").trim().toUpperCase(); // assign station ID

  /**
   * FIND STATION LINE:
   - Each station line starts with the station ID followed by a space.
   - Look for line that starts with the station ID to find the relevant data.
   - If no line is found, log a warning and return null.
   */
  const line = lines.find((ln) => ln.trim().startsWith(id + " ")); 
  if (!line) {
    console.warn("Station not found in winds text:", id);
    return null;
  }

  console.log("----- RAW LINE -----");
  console.log(line);

  const altitudeColumns = getAltitudeColumnsFromHeader(rawText);

  if (!altitudeColumns) {
    console.warn("Could not find FT altitude header.");
    return null;
  }

  // coalesce altitudes to standard 3000/6000/9000 ft for NavLog display
  // ternary operator checks if altitudes match a standard altitude otherwise defaults
  const alt1 = Number(altitudes?.alt1 ?? 3000);
  const alt2 = Number(altitudes?.alt2 ?? 6000);
  const alt3 = Number(altitudes?.alt3 ?? 9000);

  // map altitudes to their corresponding column indices in the FAA text
  const getGroup = (altitude) => {
    const col = altitudeColumns[altitude];
    if (!col) return "";

     const raw = line.slice(col.start, col.end);
    console.log(`ALT ${altitude} RAW: [${raw}]`);

    return raw.trim();
  };

  // get the groups for the three altitudes coalesced from each altitude column
  const g1 = getGroup(alt1);
  const g2 = getGroup(alt2);
  const g3 = getGroup(alt3);

  const p1 = decodeGroup(g1, alt1);
  const p2 = decodeGroup(g2, alt2);
  const p3 = decodeGroup(g3, alt3);

  return {
    alt1,
    alt2,
    alt3,

    // coalesce altitudes to empty string if no data for that altitude
    wd1: p1 ? p1.dir : "",
    ws1: p1 ? p1.spd : "",
    t1: p1 ? p1.temp : "",

    wd2: p2 ? p2.dir : "",
    ws2: p2 ? p2.spd : "",
    t2: p2 ? p2.temp : "",

    wd3: p3 ? p3.dir : "",
    ws3: p3 ? p3.spd : "",
    t3: p3 ? p3.temp : "",
  };
};



/**
 * ---------------------------------------------------------------
 * FETCH FT STATIONS FOR SELECTED REGION
 * ---------------------------------------------------------------
 */

export const fetchWindTempStations = async () => {
  const url = `${API_BASE}/api/windsaloft/stations`;

  try {
    console.log("Fetching FT stations URL:", url);

    const res = await fetch(url, {
      method: "GET",
      mode: "cors",
    });

    if (!res.ok) {
      const text = await res.text();
      console.error("Bad FT stations response:", text);
      throw new Error(`HTTP ${res.status}`);
    }

    const data = await res.json();
    console.log("FT stations data:", data);

    return data;
  } catch (err) {
    console.error("FT stations fetch failed:", err);
    return null;
  }
};

/**
 * ---------------------------------------------------------------
 * FETCH WINDS ALOFT TEXT (BY REGION)
 * ---------------------------------------------------------------
 *
 * region examples:
 *   mia, bos, chi, dfw, slc, sfo
 *
 * fcst examples:
 *   6, 12, 24
 *
 * OUTPUT:
 *   {
 *     regionInput,
 *     region,
 *     area,
 *     rawText   <-- FAA winds aloft text block
 *   }
 *
 * NOTES:
 * - Uses Vercel serverless API
 * - Returns null if fetch fails
 */
export const fetchWindsRegionText = async (region = "mia", fcst="6") => {
    const url =
    `${API_BASE}/api/windsaloft/${region}` +
    `?fcst=${encodeURIComponent(fcst)}`;


  try {
    console.log("Fetching winds URL:", url);

const res = await fetch(url, {
      method: "GET",
      mode: "cors",
    });

    console.log("Winds response status:", res.status, "ok:", res.ok);

    if (!res.ok) {
      const text = await res.text();
      console.error("Bad response body:", text);
      throw new Error(`HTTP ${res.status}`);
    }

    const data = await res.json();
    console.log("Winds JSON data:", data);

    return data;
  } catch (err) {
    console.error("Winds fetch failed:", err);
    console.error("Fetch URL that failed:", url);
    return null;
  }
};



